import React, { useRef, useState, useEffect } from 'react';
import { DateRange } from 'react-date-range';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  subMonths,
  subYears,
  isSameDay,
} from 'date-fns';

import CalenderIcon from '../../assets/icons/input-calender.png';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const Daterange = ({
  label = 'Date Range',
  name,
  from,
  to,
  disabled,
  error,
  className,
  onChange,
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedRange, setSelectedRange] = useState('This Month');

  const [dateRange, setDateRange] = useState({
    startDate: from ? new Date(from) : startOfMonth(new Date()),
    endDate: to ? new Date(to) : endOfMonth(new Date()),
    key: 'selection',
  });

  const pickerRef = useRef(null);
  const containerRef = useRef(null);

  const formatDate = (date) => format(date, 'dd/MMM/yyyy');

  useEffect(() => {
    if (onChange) {
      onChange(
        format(dateRange.startDate, 'yyyy-MM-dd'),
        format(dateRange.endDate, 'yyyy-MM-dd')
      );
    }
  }, [dateRange]);

  useEffect(() => {
    const today = new Date();

    if (
      isSameDay(dateRange.startDate, startOfMonth(today)) &&
      isSameDay(dateRange.endDate, endOfMonth(today))
    ) {
      setSelectedRange('This Month');
    } else if (
      isSameDay(dateRange.startDate, startOfMonth(subMonths(today, 1))) &&
      isSameDay(dateRange.endDate, endOfMonth(subMonths(today, 1)))
    ) {
      setSelectedRange('Last Month');
    } else if (
      isSameDay(dateRange.startDate, startOfYear(today)) &&
      isSameDay(dateRange.endDate, endOfYear(today))
    ) {
      setSelectedRange('This Year');
    } else if (
      isSameDay(dateRange.startDate, startOfYear(subYears(today, 1))) &&
      isSameDay(dateRange.endDate, endOfYear(subYears(today, 1)))
    ) {
      setSelectedRange('Last Year');
    } else {
      setSelectedRange('Custom Range');
    }
  }, [dateRange.startDate, dateRange.endDate]);

  const handleOptionSelect = (option) => {
    const today = new Date();
    let newRange = {};

    switch (option) {
      case 'This Month':
        newRange = {
          startDate: startOfMonth(today),
          endDate: endOfMonth(today),
        };
        break;
      case 'Last Month':
        const lastMonth = subMonths(today, 1);
        newRange = {
          startDate: startOfMonth(lastMonth),
          endDate: endOfMonth(lastMonth),
        };
        break;
      case 'This Year':
        newRange = {
          startDate: startOfYear(today),
          endDate: endOfYear(today),
        };
        break;
      case 'Last Year':
        const lastYear = subYears(today, 1);
        newRange = {
          startDate: startOfYear(lastYear),
          endDate: endOfYear(lastYear),
        };
        break;
      case 'Custom Range':
        setShowCalendar(true);
        setShowDropdown(false);
        return;
      default:
        return;
    }

    setSelectedRange(option);
    setDateRange({ ...newRange, key: 'selection' });
    setShowCalendar(false);
    setShowDropdown(false);
  };

  const handleApply = () => {
    setShowCalendar(false);
    // The useEffect above will handle calling onChange
  };

  const displayValue = `${formatDate(dateRange.startDate)} - ${formatDate(
    dateRange.endDate
  )}`;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target) &&
        (!pickerRef.current || !pickerRef.current.contains(e.target))
      ) {
        setShowCalendar(false);
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative w-full">
      <label className="text-sm font-medium text-[#514D4C] block mb-1">
        {label}
      </label>

      <div
        className={`relative flex items-center ${
          disabled ? 'bg-gray-100' : 'bg-white'
        } border rounded-md ${
          error ? 'border-primary' : 'border-gray-300'
        } cursor-pointer`}
        onClick={() => !disabled && setShowDropdown(!showDropdown)}
      >
        <input
          type="text"
          value={displayValue}
          readOnly
          disabled={disabled}
          className={`w-full px-3 py-2 text-sm text-[#514D4C] font-medium rounded focus:outline-none focus:ring-1 focus:ring-primary ${className}`}
        />
        <img
          src={CalenderIcon}
          alt="calendar"
          className="absolute right-2 w-5 h-5"
        />
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute z-50 bg-white shadow-lg border rounded-md mt-1 w-[300px] p-2" >
          {[
            'This Month',
            'Last Month',
            'This Year',
            'Last Year',
            'Custom Range',
          ].map((opt) => (
            <div
              key={opt}
              onClick={(e) => { e.stopPropagation(); handleOptionSelect(opt); }}
              className={`p-2 text-sm text-center cursor-pointer ${
                selectedRange === opt
                  ? 'bg-primary/85 text-white'
                  : 'hover:bg-gray-100'
              }`}
            >
              {opt}
            </div>
          ))}
        </div>
      )}

      {/* Calendar Popup */}
      {showCalendar && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-[1000]"  onClick={() => setShowCalendar(false)}>
          <div
            ref={pickerRef}
            className="bg-white rounded-lg shadow-lg p-4 relative w-auto"
             onClick={(e) => e.stopPropagation()}
          >
            <DateRange
              editableDateInputs={true}
              onChange={(item) => setDateRange(item.selection)}
              moveRangeOnFirstSelection={false}
              ranges={[dateRange]}
            />
            <div className="flex justify-end space-x-2 mt-2">
              <button
                onClick={handleApply}
                className="px-6 py-2 text-sm bg-primary font-medium text-white rounded-full"
              >
                Apply
              </button>
              <button
                onClick={() => setShowCalendar(false)}
                className="px-6 py-2 text-sm rounded-full border border-primary font-medium text-primary hover:bg-pink-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  );
};

export default Daterange;
